import { skinSeoulProductsClient } from '@/infrastructure/SkinSeoulProductsClient';
import type { SkinSeoulAdminProduct } from '@/models/ProductTypes';

/** One product an operator can point at: enough to recognise the box by eye and to file it by sku. */
export interface ProductPick {
  sku: string;
  brand: string;
  name: string;
  nameKor: string | null;
  size: string | null;
  imageUrl: string | null;
}

/** Below this a query matches half the catalog, so it is not yet a search. */
export const PRODUCT_PICK_MIN_LENGTH = 2;
export const PRODUCT_PICK_LIMIT = 8;

/** A sku is all digits; anything else is a name or a brand and only the shop search can answer it. */
const looksLikeSku = (term: string): boolean => /^\d{4,}$/.test(term);

const toPick = (product: SkinSeoulAdminProduct): ProductPick => ({
  sku: product.sku,
  brand: product.brand ?? '',
  name: product.name ?? '',
  nameKor: product.nameKor ?? null,
  size: product.size ?? null,
  imageUrl: product.productImageUrls?.find((url) => typeof url === 'string' && url.trim().length > 0) ?? null,
});

/**
 * What the shop would show for the same words: the storefront search (fuzzy, ranked, Korean included),
 * with an exact sku match placed first when the operator typed a number. A storefront outage rejects, so
 * the caller can say "could not be searched" instead of "nothing matched"; a sku lookup that fails only
 * loses the sku shortcut.
 */
export const searchProductPicks = async (term: string): Promise<ProductPick[]> => {
  const typed = term.trim();
  const [bySku, byShop] = await Promise.all([
    looksLikeSku(typed) ? skinSeoulProductsClient.searchProductsBySku(typed).catch(() => []) : Promise.resolve([]),
    skinSeoulProductsClient.searchStorefront(typed, PRODUCT_PICK_LIMIT),
  ]);
  const found = [...bySku, ...(byShop.products ?? [])].map(toPick);
  return found
    .filter((hit, index) => found.findIndex((other) => other.sku === hit.sku) === index)
    .slice(0, PRODUCT_PICK_LIMIT);
};
